import React, { useEffect, useState, useRef } from 'react';
import axios from 'axios';
import { getAccessToken } from '../../../auth';
import { envConfig } from '../../../config';
import { Logger } from '@elyx-code/common-ts-utils';
import { ILog } from '@elyx-code/definitions';
import { PublicationStep } from '../../../services/editor/publication/publication';
import { getStepNameFromDefinition } from '../utils';
import { popupNotification } from '../../../popup-notification';
import UpgradeBanner from '../../resources/UpgradeBanner';
import { useWorkspaceStore } from '../../../store/workspace';

interface IPublicationLogsViewerProps {
	step: PublicationStep;
	isOngoing: boolean;
}

const getLogColor = (type: string) => {
	switch (type) {
		case 'error':
			return '#ff6b6b';
		case 'warn':
			return '#ffd43b';
		case 'debug':
			return '#4dabf7';
		default:
			return '#a9e34b';
	}
};

const formatTimestamp = (ts: string) => {
	const d = new Date(ts);
	return (
		d.toLocaleTimeString() +
		'.' +
		String(d.getMilliseconds()).padStart(3, '0')
	);
};

export const PublicationLogsViewer: React.FC<IPublicationLogsViewerProps> = ({
	step,
	isOngoing
}) => {
	const [expanded, setExpanded] = useState(false);
	const [loading, setLoading] = useState(false);
	const [hasMore, setHasMore] = useState(true);
	const [initialFetchDone, setInitialFetchDone] = useState(isOngoing);
	const [isAtBottom, setIsAtBottom] = useState(true);

	const activeWorkspace = useWorkspaceStore((state) => state.activeWorkspace);
	const showPaywall = !(activeWorkspace?.features?.logging ?? false);

	const projectId = step.publicationRun.projectId;
	const containerRef = useRef<HTMLDivElement>(null);

	const fetchLogs = async (offset = 0) => {
		if (showPaywall) {
			if (offset === 0) {
				setLoading(true);
				setTimeout(() => {
					step.logs = [
						{
							id: '1',
							createdAt: new Date(
								Date.now() - 30000
							).toISOString(),
							text: 'INIT_START Runtime Version: nodejs:18.v106',
							type: 'info',
							parent: step.id,
							parentType: 'publication-step'
						},
						{
							id: '2',
							createdAt: new Date(
								Date.now() - 29000
							).toISOString(),
							text: 'Logger initialized. LOGS_ENABLED = true',
							type: 'debug',
							parent: step.id,
							parentType: 'publication-step'
						},
						{
							id: '3',
							createdAt: new Date(
								Date.now() - 28000
							).toISOString(),
							text: 'Starting deployment process... Resolving dependencies',
							type: 'info',
							parent: step.id,
							parentType: 'publication-step'
						},
						{
							id: '4',
							createdAt: new Date(
								Date.now() - 25000
							).toISOString(),
							text: 'Warning: Deprecated API usage detected in aws-sdk v2',
							type: 'warn',
							parent: step.id,
							parentType: 'publication-step'
						},
						{
							id: '5',
							createdAt: new Date(
								Date.now() - 20000
							).toISOString(),
							text: 'Fetching project configuration from remote state...',
							type: 'info',
							parent: step.id,
							parentType: 'publication-step'
						},
						{
							id: '6',
							createdAt: new Date(
								Date.now() - 15000
							).toISOString(),
							text: 'Validating resources... 12/12 passing',
							type: 'debug',
							parent: step.id,
							parentType: 'publication-step'
						},
						{
							id: '7',
							createdAt: new Date(
								Date.now() - 10000
							).toISOString(),
							text: 'Error in handler: ResourceNotFoundException',
							type: 'error',
							parent: step.id,
							parentType: 'publication-step'
						},
						{
							id: '8',
							createdAt: new Date(
								Date.now() - 5000
							).toISOString(),
							text: 'Retrying with exponential backoff (attempt 2/3)',
							type: 'warn',
							parent: step.id,
							parentType: 'publication-step'
						},
						{
							id: '9',
							createdAt: new Date(
								Date.now() - 2000
							).toISOString(),
							text: 'Connection re-established successfully',
							type: 'info',
							parent: step.id,
							parentType: 'publication-step'
						},
						{
							id: '10',
							createdAt: new Date(
								Date.now() - 1000
							).toISOString(),
							text: 'CloudFormation stack update initiated...',
							type: 'info',
							parent: step.id,
							parentType: 'publication-step'
						},
						{
							id: '11',
							createdAt: new Date(Date.now() - 500).toISOString(),
							text: 'Waiting for stack CREATE_COMPLETE state',
							type: 'debug',
							parent: step.id,
							parentType: 'publication-step'
						},
						{
							id: '12',
							createdAt: new Date().toISOString(),
							text: 'Deployment successful. Cleaning up temporary artifacts.',
							type: 'info',
							parent: step.id,
							parentType: 'publication-step'
						}
					] as any;
					setHasMore(false);
					setLoading(false);
					setInitialFetchDone(true);
				}, 300);
			}
			return;
		}

		if (!projectId) return;

		setLoading(true);
		try {
			const itemsPerPage = 30;
			const response = await axios.get(
				`${envConfig.API_BASE_URL}/publishing-service/v1/publications/${projectId}/logs?parent=${step.id}&parentType=publication-step&offset=${offset}&limit=${itemsPerPage}&direction=latest`,
				{
					headers: {
						Authorization: 'Bearer ' + (await getAccessToken())
					}
				}
			);

			const { items, total } = response.data;

			const chronologicalItems = (items as ILog[]).reverse();

			if (offset === 0) {
				step.logs = chronologicalItems;
			} else {
				step.logs = [...chronologicalItems, ...step.logs];
			}

			if (step.logs.length >= total) {
				setHasMore(false);
			}
		} catch (error) {
			Logger.error('Failed to fetch step logs', error);
		} finally {
			setLoading(false);
			if (offset === 0) {
				setInitialFetchDone(true);
			}
		}
	};

	useEffect(() => {
		if (!isOngoing && !initialFetchDone) {
			fetchLogs(0);
		}
	}, [isOngoing, initialFetchDone]);

	const wasOngoing = useRef(isOngoing);
	useEffect(() => {
		if (wasOngoing.current && !isOngoing) {
			fetchLogs(step.logs.length);
		}
		wasOngoing.current = isOngoing;
	}, [isOngoing]);

	const scrollToBottom = () => {
		if (containerRef.current) {
			containerRef.current.scrollTop = containerRef.current.scrollHeight;
		}
	};

	const scrollToTop = () => {
		if (containerRef.current) {
			containerRef.current.scrollTop = 0;
		}
	};

	const copyToClipboard = () => {
		const text = step.logs
			.map(
				(l: ILog) =>
					`[${new Date(l.createdAt).toLocaleTimeString()}] ${l.text}`
			)
			.join('\n');
		navigator.clipboard.writeText(text);

		popupNotification({
			color: 'success',
			text: `${step.logs.length} log ${step.logs.length === 1 ? 'line' : 'lines'} copied to clipboard`
		});
	};

	// Auto-scroll when new logs arrive if user is at the bottom
	useEffect(() => {
		if (isOngoing && expanded && isAtBottom) {
			setTimeout(scrollToBottom, 50);
		}
	}, [step.logs.length, expanded, isOngoing, isAtBottom]);

	const handleScroll = () => {
		if (containerRef.current) {
			const { scrollTop, scrollHeight, clientHeight } =
				containerRef.current;
			// 10px threshold for being at the bottom
			if (scrollHeight - scrollTop - clientHeight < 10) {
				setIsAtBottom(true);
			} else {
				setIsAtBottom(false);
			}
		}
	};

	if (!initialFetchDone) {
		return null;
	}

	if (step.logs.length === 0 && (!loading || initialFetchDone)) {
		return null;
	}

	return (
		<div
			style={{
				marginTop: '8px',
				display: 'flex',
				flexDirection: 'column',
				gap: '4px'
			}}
		>
			<div
				className="step-logs-viewer-toggle"
				onClick={() => setExpanded(!expanded)}
				style={{
					display: 'flex',
					alignItems: 'center',
					gap: '4px',
					cursor: 'pointer',
					color: '#6b7280',
					fontSize: '12px',
					userSelect: 'none',
					padding: '4px 0'
				}}
			>
				<i
					className={
						expanded
							? 'fa-solid fa-chevron-down'
							: 'fa-solid fa-chevron-right'
					}
					style={{ fontSize: '9px', width: '12px' }}
				/>
				<span>{expanded ? 'Hide Logs' : 'View Logs'}</span>
			</div>

			{expanded && (
				<div
					style={{
						position: 'relative',
						display: 'flex',
						flexDirection: 'column',
						border: '1px solid #2d2d2d',
						borderRadius: '10px',
						backgroundColor: '#0d1117',
						boxShadow: '0 4px 12px rgba(0,0,0,0.15)',
						overflow: 'hidden',
						marginTop: '4px'
					}}
				>
					{/* Terminal Header */}
					<div
						style={{
							backgroundColor: '#161b22',
							padding: '8px 16px',
							display: 'flex',
							alignItems: 'center',
							justifyContent: 'space-between',
							borderBottom: '1px solid #21262d'
						}}
					>
						<div
							style={{
								display: 'flex',
								alignItems: 'center',
								gap: '6px'
							}}
						>
							<div
								style={{
									width: '12px',
									height: '12px',
									borderRadius: '50%',
									backgroundColor: '#ff5f56'
								}}
							/>
							<div
								style={{
									width: '12px',
									height: '12px',
									borderRadius: '50%',
									backgroundColor: '#ffbd2e'
								}}
							/>
							<div
								style={{
									width: '12px',
									height: '12px',
									borderRadius: '50%',
									backgroundColor: '#27c93f'
								}}
							/>
							<span
								style={{
									marginLeft: '12px',
									color: '#8b949e',
									fontFamily: 'monospace',
									fontSize: '11px'
								}}
							>
								{getStepNameFromDefinition(step.stepType) ||
									'Publication logs'}
							</span>
						</div>

						{/* Actions */}
						<div
							style={{
								display: 'flex',
								alignItems: 'center',
								gap: '12px'
							}}
						>
							<button
								onClick={copyToClipboard}
								title="Copy to clipboard"
								style={{
									background: 'none',
									border: 'none',
									cursor: 'pointer',
									color: '#8b949e',
									fontSize: '12px',
									display: 'flex',
									alignItems: 'center',
									gap: '4px',
									padding: 0
								}}
								onMouseEnter={(e) =>
									(e.currentTarget.style.color = '#c9d1d9')
								}
								onMouseLeave={(e) =>
									(e.currentTarget.style.color = '#8b949e')
								}
							>
								<i className="fa-light fa-copy" /> Copy
							</button>
							<button
								onClick={scrollToTop}
								title="Scroll to top"
								style={{
									background: 'none',
									border: 'none',
									cursor: 'pointer',
									color: '#8b949e',
									fontSize: '12px',
									display: 'flex',
									alignItems: 'center',
									gap: '4px',
									padding: 0
								}}
								onMouseEnter={(e) =>
									(e.currentTarget.style.color = '#c9d1d9')
								}
								onMouseLeave={(e) =>
									(e.currentTarget.style.color = '#8b949e')
								}
							>
								<i className="fa-light fa-arrow-up" /> Top
							</button>
							<button
								onClick={scrollToBottom}
								title="Scroll to bottom"
								style={{
									background: 'none',
									border: 'none',
									cursor: 'pointer',
									color: '#8b949e',
									fontSize: '12px',
									display: 'flex',
									alignItems: 'center',
									gap: '4px',
									padding: 0
								}}
								onMouseEnter={(e) =>
									(e.currentTarget.style.color = '#c9d1d9')
								}
								onMouseLeave={(e) =>
									(e.currentTarget.style.color = '#8b949e')
								}
							>
								<i className="fa-light fa-arrow-down" /> Bottom
							</button>
							<span
								style={{
									fontSize: '11px',
									color: '#8b949e',
									fontFamily: 'monospace'
								}}
							>
								{step.logs.length} events
							</span>
						</div>
					</div>

					{/* Terminal Body */}
					<div
						ref={containerRef}
						onScroll={handleScroll}
						style={{
							padding: '16px',
							maxHeight: showPaywall ? '400px' : '250px',
							minHeight: showPaywall ? '400px' : 'auto',
							overflowY: 'auto',
							fontFamily:
								"'Fira Code', 'Courier New', Courier, monospace",
							fontSize: '11px',
							lineHeight: '1.6',
							color: '#c9d1d9',
							display: 'flex',
							flexDirection: 'column',
							gap: '6px',
							filter: showPaywall ? 'blur(3px)' : 'none',
							opacity: showPaywall ? 0.4 : 1,
							pointerEvents: showPaywall ? 'none' : 'auto',
							userSelect: showPaywall ? 'none' : 'auto'
						}}
					>
						{hasMore && step.logs.length > 0 && !isOngoing && (
							<div
								style={{
									display: 'flex',
									justifyContent: 'center',
									marginBottom: '8px'
								}}
							>
								<button
									onClick={() => fetchLogs(step.logs.length)}
									disabled={loading}
									style={{
										background: 'transparent',
										border: '1px solid #30363d',
										borderRadius: '4px',
										color: '#8b949e',
										padding: '4px 12px',
										fontSize: '11px',
										cursor: loading
											? 'not-allowed'
											: 'pointer'
									}}
									onMouseEnter={(e) => {
										if (!loading) {
											e.currentTarget.style.borderColor =
												'#8b949e';
											e.currentTarget.style.color =
												'#c9d1d9';
										}
									}}
									onMouseLeave={(e) => {
										if (!loading) {
											e.currentTarget.style.borderColor =
												'#30363d';
											e.currentTarget.style.color =
												'#8b949e';
										}
									}}
								>
									{loading
										? 'Loading...'
										: 'Load earlier logs'}
								</button>
							</div>
						)}
						{step.logs.length === 0 && loading && (
							<div
								style={{
									display: 'flex',
									alignItems: 'center',
									justifyContent: 'center',
									color: '#8b949e'
								}}
							>
								<i
									className="fa-solid fa-spinner fa-spin"
									style={{ marginRight: '8px' }}
								></i>
								Loading events...
							</div>
						)}
						{step.logs.map((log: ILog) => {
							const color = getLogColor(log.type || 'info');
							return (
								<div
									key={log.id}
									style={{
										display: 'flex',
										alignItems: 'flex-start',
										gap: '12px',
										wordBreak: 'break-all',
										whiteSpace: 'pre-wrap',
										borderBottom:
											'1px solid rgba(255,255,255,0.02)',
										paddingBottom: '4px'
									}}
								>
									{/* Pointing to line number */}
									<span
										style={{
											color: '#58a6ff',
											flexShrink: 0,
											userSelect: 'none'
										}}
									>
										[{formatTimestamp(log.createdAt)}]
									</span>

									{/* Log Level Chip */}
									<span
										style={{
											color: color,
											fontWeight: 600,
											flexShrink: 0,
											userSelect: 'none',
											minWidth: '55px',
											textTransform: 'uppercase'
										}}
									>
										{log.type || 'INFO'}
									</span>

									{/* Log Message */}
									<span style={{ flex: 1 }}>{log.text}</span>
								</div>
							);
						})}
					</div>

					{showPaywall && (
						<div
							style={{
								position: 'absolute',
								top: '50%',
								left: '50%',
								transform: 'translate(-50%, -50%)',
								zIndex: 100,
								width: '90%',
								maxWidth: '480px',
								display: 'flex',
								flexDirection: 'column',
								alignItems: 'center',
								justifyContent: 'center',
								pointerEvents: 'none',
								backgroundColor: 'transparent'
							}}
						>
							<UpgradeBanner />
						</div>
					)}
				</div>
			)}
		</div>
	);
};

export default PublicationLogsViewer;
